import * as React from "react";
import { Input } from "./input";
import { Label } from "./label";
import { cn } from "@/lib/utils";
import { Check, X, Mail } from "lucide-react";

export interface EmailInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  onValidationChange?: (isValid: boolean) => void;
  showValidation?: boolean;
  label?: string;
}

const EmailInput = React.forwardRef<HTMLInputElement, EmailInputProps>(
  (
    { className, onValidationChange, showValidation = true, label, ...props },
    ref,
  ) => {
    const [value, setValue] = React.useState(props.value || "");
    const [isValid, setIsValid] = React.useState(false);
    const [isTouched, setIsTouched] = React.useState(false);

    const validateEmail = (email: string) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setValue(newValue);

      const valid = validateEmail(newValue);
      setIsValid(valid);

      if (onValidationChange) {
        onValidationChange(valid);
      }

      if (props.onChange) {
        props.onChange(e);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsTouched(true);
      const newValue = e.target.value;
      const valid = validateEmail(newValue);
      setIsValid(valid);

      if (props.onBlur) {
        props.onBlur(e);
      }
    };

    const showValidationIcon = showValidation && isTouched && value;

    return (
      <div className="space-y-2">
        {label && <Label htmlFor={props.id}>{label}</Label>}
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="email"
            className={cn(
              "pl-10 pr-10",
              isTouched &&
                value &&
                !isValid &&
                "border-red-500 focus:border-red-500",
              isTouched &&
                value &&
                isValid &&
                "border-green-500 focus:border-green-500",
              className,
            )}
            ref={ref}
            {...props}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={props.placeholder || "Enter email address"}
          />
          {showValidationIcon && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {isValid ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <X className="h-4 w-4 text-red-500" />
              )}
            </div>
          )}
        </div>
        {showValidation && isTouched && value && !isValid && (
          <p className="text-sm text-red-500">
            Please enter a valid email address
          </p>
        )}
      </div>
    );
  },
);

EmailInput.displayName = "EmailInput";

export { EmailInput };
